/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListMcpServersRequest,
  ListMcpServersSecurity,
} from "../models/operations/listmcpservers.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildMcpServersQuery,
  McpServersQueryData,
  prefetchMcpServers,
  queryKeyMcpServers,
} from "./mcpServers.core.js";
export {
  buildMcpServersQuery,
  type McpServersQueryData,
  prefetchMcpServers,
  queryKeyMcpServers,
};

export type McpServersQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listMcpServers mcpServers
 *
 * @remarks
 * List MCP servers for a project. Accepts optional remote_mcp_server_id, tunneled_mcp_server_id, toolset_id, or unproxied_mcp_server_id filters to scope the result to a single backend; at most one filter may be supplied since the backends are mutually exclusive.
 */
export function useMcpServers(
  request?: ListMcpServersRequest | undefined,
  security?: ListMcpServersSecurity | undefined,
  options?: QueryHookOptions<McpServersQueryData, McpServersQueryError>,
): UseQueryResult<McpServersQueryData, McpServersQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildMcpServersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listMcpServers mcpServers
 *
 * @remarks
 * List MCP servers for a project. Accepts optional remote_mcp_server_id, tunneled_mcp_server_id, toolset_id, or unproxied_mcp_server_id filters to scope the result to a single backend; at most one filter may be supplied since the backends are mutually exclusive.
 */
export function useMcpServersSuspense(
  request?: ListMcpServersRequest | undefined,
  security?: ListMcpServersSecurity | undefined,
  options?: SuspenseQueryHookOptions<McpServersQueryData, McpServersQueryError>,
): UseSuspenseQueryResult<McpServersQueryData, McpServersQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildMcpServersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setMcpServersData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      remoteMcpServerId?: string | undefined;
      tunneledMcpServerId?: string | undefined;
      toolsetId?: string | undefined;
      unproxiedMcpServerId?: string | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: McpServersQueryData,
): McpServersQueryData | undefined {
  const key = queryKeyMcpServers(...queryKeyBase);

  return client.setQueryData<McpServersQueryData>(key, data);
}

export function invalidateMcpServers(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      remoteMcpServerId?: string | undefined;
      tunneledMcpServerId?: string | undefined;
      toolsetId?: string | undefined;
      unproxiedMcpServerId?: string | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "mcpServers", "list", ...queryKeyBase],
  });
}

export function invalidateAllMcpServers(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "mcpServers", "list"],
  });
}
